// Third-party
import React, { Component, ReactElement } from 'react';
import { connect } from 'react-redux';

// Redux
import { ReduxState } from '@/redux/modules/types';
import { addEnterpriseMember, deleteEnterpriseMember, modifyEnterpriseMember } from '@/redux/modules/enterprise/admin/members';
import { addEnterpriseGroupMember, deleteEnterpriseGroupMember } from '@/redux/modules/enterprise/admin/groups';

// Components
import TablePageLayout from '@/views/containers/layouts/TablePageLayout';
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import EnterpriseDetailsSection from '@/views/containers/admin/enterprise/EnterpriseDetails';
import EnterpriseGroupSettings from '@/views/containers/admin/group/EnterpriseGroupSettings';
import { PrimaryButton } from '@/views/components/buttons';
import { AddEnterpriseMemberModal } from '@/views/components/modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';
import SearchInput from '@/views/components/inputs/SearchInput';
import ModifyEnterpriseMemberModal from '@/views/components/modals/ModifyEnterpriseMemberModal';
import AddEnterpriseGroupMemberModal from '@/views/components/modals/AddEnterpriseGroupMemberModal';
import TableMenu from '@/views/components/data/DataTableV2/TableMenu';
import { Breadcrumb, ControlLinkItem, SecondaryNavigation, Tabs } from '@/views/components/navigation';
import { GenericEmptyState } from '@/views/components/misc';
import { InfoTooltip } from '@/views/components/tooltips';
import { LoadingSpinner } from '@/views/components/common';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import notification from '@/utils/notification';
import { searchObjectsOnFields } from '@/utils/search';
import { ENTERPRISE_GROUP_NAME, getEnterpriseGroup, isEnterpriseGroup } from '@/utils/enterprise';
import { PERMISSIONS, userHasPermission, ENTERPRISE_ADMIN_PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { getValueColumnDef, getCustomColumnDef, prepareMenuItems } from '@/utils/data-table';

// Types
import { CellContext, ColumnDef } from '@tanstack/react-table';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import EnterpriseAdminGroup from '@/types/enterprise-admin-group';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import EnterpriseGroup from '@/types/enterprise-group';
import EnterpriseInfos from '@/types/enterprise-infos';
import EnterpriseMember from '@/types/enterprise-member';
import EnterpriseTabOption from '@/types/enterprise-tab-option';
import NavigationItem from '@/types/navigation-item';
import User from '@/types/user';

// Legacy
import LegacyEnterpriseMembers from '@/views/containers/admin/enterprise/LegacyEnterpriseMembers';
import { AvatarGroup } from '@/views/components/design-system-v2';
import { getAvatarsFromDelegateAccess } from '@/views/containers/admin/enterprise/enterprise-members/utils/delegate';
import { LicenseType } from '@/types/license-type';

export enum MembersTableFields {
  Delegate = 'delegateFor',
  Email = 'email',
  Fname = 'fname',
  Id = '_id',
  Lname = 'lname',
  NameId = 'nameId',
  Role = 'role',
  Uid = 'uid',
}

type State = {
  showAddMemberModal: boolean;
  showModifyMemberModal: boolean;
  showDeleteMembersModal: boolean;
  membersToDelete: EnterpriseMember[];
  memberToUpdate?: EnterpriseMember;
  query: string;
  memberDisplayMultiplier: number;
};

type StateProps = {
  adminDetails: EnterpriseAdminDetails;
  enterprise?: EnterpriseDetails; // Optional for now due to legacy components. Will be required after legacy components are removed.
  group: EnterpriseAdminGroup;
  user: User;
};

type ParentProps = {
  adminEnterpriseTabOptions?: EnterpriseTabOption[]; // Optional for now due to legacy components. Will be required after legacy components are removed.
  isGroup?: boolean;
  pills?: NavigationItem[]; // Optional for now due to legacy components. Will be required after legacy components are removed.
  selectedAdminEnterpriseTab?: string; // Optional for now due to legacy components. Will be required after legacy components are removed.
  onSave?: (info: EnterpriseInfos) => Promise<EnterpriseInfos>; // Optional for now due to legacy components. Will be required after legacy components are removed.
};

type DispatchProps = {
  addEnterpriseGroupMember: (groupId: string, userId: string) => Promise<EnterpriseMember>;
  deleteEnterpriseGroupMember: (groupId: string, userId: string) => Promise<void>;
  addEnterpriseMember: (fname: string, lname: string, email: string, uid: string, nameId: string, licenseType?: LicenseType) => Promise<EnterpriseMember>;
  modifyEnterpriseMember: (userId: string, uid: string, nameId: string, licenseType?: LicenseType) => Promise<void>;
  deleteEnterpriseMember: (userId: string) => Promise<void>;
};

type Props = ParentProps & StateProps & DispatchProps;

class EnterpriseMembers extends Component<Props, State> {
  readonly i18nBasePath = 'Admin.Enterprise.Table';

  readonly state: State = {
    showAddMemberModal: false,
    showModifyMemberModal: false,
    showDeleteMembersModal: false,
    membersToDelete: [],
    memberToUpdate: undefined,
    query: '',
    memberDisplayMultiplier: 1,
  };

  removeSelectedMembers = () => {
    const { user, group } = this.props;
    const { membersToDelete } = this.state;

    if (this == null && user == null) {
      return;
    }

    // Prevent admins from deleting themselves from the enterprise.
    if (group.name === ENTERPRISE_GROUP_NAME && membersToDelete.some(member => member._id === user.id)) {
      notification.error(formatMessage('Admin.Enterprise.SelfDeleteError'));
      return;
    }

    this.hideDeleteMembersModal();

    for (const member of membersToDelete) {
      if (group.name === ENTERPRISE_GROUP_NAME) {
        this.props.deleteEnterpriseMember(member._id);
      } else {
        this.props.deleteEnterpriseGroupMember(group._id, member._id);
      }
    }
  };

  getRoleColumn = (data: CellContext<EnterpriseMember, any>) => {
    const features = data.row.original?.features?.filter((feature: string) => ENTERPRISE_ADMIN_PERMISSIONS.includes(feature));
    if (features.length > 0) {
      const tooltip: string[] = [];
      for (const feature of features) {
        tooltip.push(formatMessage(`AdminPermissions.${feature}`));
      }
      return (
        <>
          <span data-private data-dd-privacy="mask">
            Admin&nbsp;
          </span>
          <InfoTooltip message={tooltip.join(', ')} />
        </>
      );
    }

    const licenseType = data.row.original?.licenseType;
    if (licenseType === 'LIMITED') {
      return (
        <span>
          <FormattedMessage id="Admin.Enterprise.LicenseTypeLimited" />
        </span>
      );
    }

    return (
      <span>
        <FormattedMessage id="Admin.Enterprise.LicenseTypeRegular" />
      </span>
    );
  };

  getDelegateColumn = (data: CellContext<EnterpriseMember, any>) => {
    const avatars = getAvatarsFromDelegateAccess(data.row.original?.delegateFor);
    return <AvatarGroup avatars={avatars} options={{ maxVisible: 6 }} />;
  };

  getMenuActions = (data: CellContext<EnterpriseMember, any>, isAllowedToEdit: boolean, isAllowedToDelete: boolean) => [
    {
      icon: 'icon-ds-edit',
      isAllowed: (): boolean => isAllowedToEdit,
      key: 'editSingle',
      value: formatMessage('Admin.Enterprise.EditMember'),
      onClick: () => this.onClickModifyMember(data.row.original),
    },
    {
      className: 'destructive',
      icon: 'icon-ds-delete',
      isAllowed: (): boolean => isAllowedToDelete,
      key: 'deleteSingle',
      value: formatMessage('Admin.Enterprise.DeleteMember'),
      onClick: () => this.showDeleteMembersModal([data.row.original]),
    },
  ];

  getActionItems = (data: CellContext<EnterpriseMember, any>, group: EnterpriseGroup, user: User) => {
    const hasMemberRemovePermission = userHasPermissionEnabled(
      user,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
    );

    const hasEditMemberIdPermission = userHasPermissionEnabled(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_UID, PERMISSIONS.ADMIN_SECTION_SHOW_USER_NAME_ID);

    const isAllowedToDelete = hasMemberRemovePermission && (!isEnterpriseGroup(group) || data.row.original._id !== user.id);
    const isAllowedToEdit = hasEditMemberIdPermission && isEnterpriseGroup(group);

    const menuActions = this.getMenuActions(data, isAllowedToEdit, isAllowedToDelete);
    const preparedMenuItems = prepareMenuItems(menuActions, data);
    return <TableMenu menuItems={preparedMenuItems} />;
  };

  getTableColumns = () => {
    const { group, user } = this.props;

    const columns: ColumnDef<EnterpriseMember>[] = [
      getValueColumnDef(MembersTableFields.Fname, this.i18nBasePath),
      getValueColumnDef(MembersTableFields.Lname, this.i18nBasePath),
      getValueColumnDef(MembersTableFields.Email, this.i18nBasePath),
    ];

    columns.push(getCustomColumnDef(MembersTableFields.Role, this.i18nBasePath, original => this.getRoleColumn(original)));

    columns.push(getCustomColumnDef(MembersTableFields.Delegate, this.i18nBasePath, original => this.getDelegateColumn(original)));

    if (userHasPermission(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_UID) && group.name === ENTERPRISE_GROUP_NAME) {
      columns.push(getValueColumnDef(MembersTableFields.Uid, this.i18nBasePath));
    }

    if (userHasPermission(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_NAME_ID) && group.name === ENTERPRISE_GROUP_NAME) {
      columns.push(getValueColumnDef(MembersTableFields.NameId, this.i18nBasePath));
    }

    columns.push(getCustomColumnDef(MembersTableFields.Id, this.i18nBasePath, data => this.getActionItems(data, group, user), { header: '', size: 96 }));

    return columns;
  };

  showDeleteMembersModal = (membersToDelete: EnterpriseMember[]) => {
    this.setState(() => ({
      showDeleteMembersModal: true,
      membersToDelete,
    }));
  };

  showAddMemberModal = () => {
    this.setState({ showAddMemberModal: true });
  };

  hideAddMemberModal = () => {
    this.setState({ showAddMemberModal: false });
  };

  showModifyMemberModal = () => {
    this.setState({ showModifyMemberModal: true });
  };

  hideModifyMemberModal = () => {
    this.setState({ showModifyMemberModal: false });
  };

  hideDeleteMembersModal = () => {
    this.setState({ showDeleteMembersModal: false });
  };

  onAddGroupMember = (memberId: string) => {
    const { group } = this.props;

    return this.props.addEnterpriseGroupMember(group._id, memberId).then(() => {
      this.hideAddMemberModal();
    });
  };

  filterMembersFromQuery = (members: EnterpriseMember[], query: string) => {
    if (!query) {
      return members;
    }

    const searchableFields = ['fname', 'lname', 'fullname', 'email', 'uid', 'nameId'];

    // Generate fullname for search purposes.
    const objs = members.map(m => ({
      ...m,
      fullname: `${m.fname} ${m.lname}`,
    }));

    return searchObjectsOnFields(query, objs, searchableFields, false);
  };

  onMembersSearch = (query: string) => {
    this.setState({ query, memberDisplayMultiplier: 1 });
  };

  onAddMember = (fname: string, lname: string, email: string, uid?: string, nameId?: string, licenseType?: LicenseType) =>
    this.props.addEnterpriseMember(fname, lname, email, uid != null ? uid : '', nameId != null ? nameId : '', licenseType);

  onModifyMember = (_id: string, uid?: string, nameId?: string, licenseType?: LicenseType) =>
    this.props.modifyEnterpriseMember(_id, uid != null ? uid : '', nameId != null ? nameId : '', licenseType);

  loadMoreMembers = () => {
    const { memberDisplayMultiplier } = this.state;
    const multiplier = memberDisplayMultiplier + 1;
    this.setState({ memberDisplayMultiplier: multiplier });
  };

  onClickModifyMember = (member: EnterpriseMember): void => {
    this.setState({ memberToUpdate: member });
    this.showModifyMemberModal();
  };

  useMemberUID = () => userHasPermission(this.props.user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_UID);

  useMemberNameID = () => userHasPermission(this.props.user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_NAME_ID);

  getEmptyStateCard = (query: string) => {
    const state = !query ? 'NoMembers' : 'NoResults';
    return (
      <div className="empty-state-card">
        <GenericEmptyState header={formatMessage(`Admin.Enterprise.${state}.Title`)} body={formatMessage(`Admin.Enterprise.${state}.Text`)} />
      </div>
    );
  };

  getDetailsSection = () => {
    if (this.props.isGroup) {
      const breadcrumbs = [
        {
          value: formatMessage('Admin.Enterprise.Groups.Title'),
          redirect: '/admin/groups',
        },
        {
          value: this.props.group?.name,
        },
      ];
      return (
        <>
          <Breadcrumb replace items={breadcrumbs} />
          <EnterpriseGroupSettings onSave={this.props.onSave} group={this.props.group} user={this.props.user} />
        </>
      );
    }
    const { adminDetails, enterprise } = this.props;
    return (
      <EnterpriseDetailsSection
        className="info-element"
        enterprise={enterprise}
        adminDetails={adminDetails}
        onSave={this.props.onSave}
        user={this.props.user}
      />
    );
  };

  render() {
    const { adminDetails, enterprise, group, isGroup, user } = this.props;
    const { showAddMemberModal, showModifyMemberModal, showDeleteMembersModal, query, memberDisplayMultiplier } = this.state;

    let memberSrc = [] as EnterpriseMember[];
    if (group != null) {
      memberSrc = group.members;
    } else if (adminDetails != null) {
      memberSrc = getEnterpriseGroup(adminDetails).members;
    }
    const filteredMembers = this.filterMembersFromQuery(memberSrc, query);
    const members = filteredMembers.slice(0, memberDisplayMultiplier * 10);
    const hasMore = filteredMembers.length > members.length;
    const hasMembers = members && members.length > 0;
    const useMemberUID = this.useMemberUID();
    const useMemberNameID = this.useMemberNameID();
    const hasMemberAddPermission = userHasPermissionEnabled(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD);
    const hasDelegateManagementPermission = userHasPermissionEnabled(user, PERMISSIONS.DELEGATE_MANAGEMENT);

    const name = isGroup ? group.name : enterprise?.name;
    const subtitle = formatMessage('Members.EnterpriseTitle', { name: name ?? '', count: name?.endsWith('s') ? 1 : 0 });

    return (
      <>
        <TablePageLayout
          detailsElement={this.getDetailsSection()}
          mainClassName="enterprise-members"
          pageTitle="Admin.PageTitle"
          pillsFilterElement={this.props.pills && <SecondaryNavigation className="navigation-pills" items={this.props.pills} />}
          subPageTitle={subtitle ?? ''}
          searchInputElement={
            <SearchInput
              className="search-input-v2"
              value={this.state.query}
              onChange={this.onMembersSearch}
              placeholder={formatMessage('Admin.Enterprise.SearchMembers')}
            />
          }
          pageActionsElement={
            <PrimaryButton className="primary-v2 add-member" disabled={!hasMemberAddPermission} onClick={this.showAddMemberModal}>
              <i className="icon-plus" />
              <FormattedMessage id="Admin.Enterprise.AddMember" />
            </PrimaryButton>
          }
          tabsElement={
            this.props.adminEnterpriseTabOptions && (
              <Tabs className="tabs-v2" selected={this.props.selectedAdminEnterpriseTab}>
                {this.props.adminEnterpriseTabOptions.map(option => (
                  <ControlLinkItem ariaLabel={option.label} key={option.id} route={option.value || '#'} value={option.id}>
                    <FormattedMessage id={option.i18nPath} />
                  </ControlLinkItem>
                ))}
              </Tabs>
            )
          }
          loadingElement={<LoadingSpinner className="initial-loading" message={formatMessage('Contacts.ContactTabs.Portfolios.LoadingPortfolios')} />}
          tableElement={
            <div className="table-container">
              <DataTableV2
                columns={this.getTableColumns()}
                data={members}
                enableColumnFreeze={false}
                infiniteScrollOptions={{ show: hasMore, onEnter: this.loadMoreMembers }}
              />
            </div>
          }
          hasData={hasMembers}
          noDataElement={this.getEmptyStateCard(query)}
        />

        {group && group.name === ENTERPRISE_GROUP_NAME ? (
          <>
            <AddEnterpriseMemberModal
              show={showAddMemberModal}
              onCancel={this.hideAddMemberModal}
              onSave={this.onAddMember}
              useMemberUID={useMemberUID}
              useMemberNameID={useMemberNameID}
              useLicenseType={hasDelegateManagementPermission}
            />

            {(useMemberUID || useMemberNameID) && this.state.memberToUpdate && (
              <ModifyEnterpriseMemberModal
                user={user}
                show={showModifyMemberModal}
                onCancel={this.hideModifyMemberModal}
                onSave={this.onModifyMember}
                memberToUpdate={this.state.memberToUpdate}
              />
            )}
          </>
        ) : (
          <AddEnterpriseGroupMemberModal
            show={showAddMemberModal}
            onCancel={this.hideAddMemberModal}
            onAdd={this.onAddGroupMember}
            group={group}
            adminDetails={adminDetails}
          />
        )}

        <DestructiveModal
          open={showDeleteMembersModal}
          onOpenChange={open => !open && this.hideDeleteMembersModal()}
          title={formatMessage('Enterprise.DeleteMembersModal.Title')}
          content={
            <Stack gap="150">
              <FormattedMessage id="Enterprise.DeleteMembersModal.Body" />
              <ul>
                {this.state.membersToDelete.map(m => (
                  <li key={m._id}>{`${m.fname ?? ''} ${m.lname ?? ''}`}</li>
                ))}
              </ul>
              <FormattedMessage id="Enterprise.DeleteMembersModal.Confirmation" />
            </Stack>
          }
          confirmButtonText={formatMessage('Modal.Default.Confirm')}
          cancelButtonText={formatMessage('Common.Cancel')}
          onConfirm={this.removeSelectedMembers}
          onCancel={this.hideDeleteMembersModal}
        />
      </>
    );
  }
}

const EnterpriseMembersComponent = (props: Props): ReactElement => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EnterpriseMembers {...props} />;
  }
  return <LegacyEnterpriseMembers {...props} />;
};

const mapStateToProps = (state: ReduxState, ownProps: StateProps) => {
  let { group } = ownProps;

  if (!group) {
    const groups = state.enterprise.admin ? state.enterprise.admin.groups : [];
    group = groups.find(g => g.name === ENTERPRISE_GROUP_NAME) as EnterpriseAdminGroup;
  }

  return {
    user: state.auth.user as User,
    adminDetails: state.enterprise.admin,
    group,
  };
};

export default connect(mapStateToProps, {
  addEnterpriseGroupMember,
  deleteEnterpriseGroupMember,
  addEnterpriseMember,
  modifyEnterpriseMember,
  deleteEnterpriseMember,
})(EnterpriseMembersComponent);
